import _ from 'lodash';
import * as types from 'src/store/types';
import { NORMAL_AD_SOURCES, VIDEO_ASYNC_RENDER_FILL_ACTION_TYPE, AD_TYPE } from 'src/const/state/creative';
import { INIT_ADS, SPLIT_TEST_UNIT } from './state';
import { transformDetail2Ad, isValidAdSource } from './helpers';

export default {
  [types.SET_CREATIVE_PREVIEW_INDEX](state, data) {
    state.creativePreviewIndex = data;
  },
  [types.SET_SHA1_KEY](state, data) {
    state.sha1Key = data;
  },
  [types.SET_CREATIVE_DOMAIN](state, data) {
    state.domainCancelToken = data;
  },
  [types.SET_APP_TYPES](state, data) {
    if (Array.isArray(data)) {
      state.appTypes = data;
    } else {
      state.appTypes = [data * 1000];
    }
  },
  [types.SET_SELECTED_FORM](state, { splitTestTab, data }) {
    if (splitTestTab === 1) {
      state.selectedFormObjectOppo = data;
    } else {
      state.selectedFormObject = data;
    }
  },
  // 重置整体ads数组
  // TODO: 这里应该清空所有creative/create 的vuex数据，避免第二次新建还使用上一次的vuex数据
  [types.RESET_ADS](state) {
    state.ads = _.cloneDeep(INIT_ADS);
    state.activeSplitTestTab = 0;
    state.appInfo = {}; // FIXED: 重置时清空，否则创建完app类型的创意，在创建website创意，会回显click_url
  },
  [types.SET_IS_NEW_STRUCTURE](state, data) {
    state.isNewStructure = data;
  },
  // vue监听不到数组的变化, 为了兼容split test场景
  [types.SET_EXTERNAL_URL_DOMAIN](state, data) {
    Vue.set(state.externalUrlDomain[data.splitTestTab], data.index, data.domain);
  },
  // 设置整体ads数组
  [types.SET_ADS](state, ads) {
    state.ads = _.cloneDeep(ads || INIT_ADS);
    state.activeSplitTestTab = 0;
  },
  // 添加一个split test对照组
  [types.ADD_SPLIT_TEST_UNIT](state) {
    state.ads.push(_.cloneDeep(SPLIT_TEST_UNIT));
  },
  // 切换对照组
  [types.UPDATE_SPLIT_TEST_TAB](state, tabIndex) {
    state.activeSplitTestTab = tabIndex || 0;
  },
  // 映射所有ad的call to action
  [types.TRANSFORM_ALL_AD_CTA](state, transformer = cta => cta) {
    const handleSourceTarget = (sourceTarget) => {
      // ad内包含多种形式的素材，单图、单视频、商品库图片、商品库视频，每种形式的cta都改掉
      Object.keys(sourceTarget)
        .filter(key => key !== 'adType')
        .forEach((key) => {
          const adDetail = sourceTarget[key];
          adDetail.call_to_action = transformer(adDetail.call_to_action, adDetail.call_to_action_type);
        });
    };

    // 处理每个实验对照组
    const handleAdsUnit = (adsUnit) => {
      adsUnit.forEach((ad) => {
        Object.keys(ad)
          .filter(key => key !== 'adSource')
          .forEach((key) => { // 每种ad source都改掉
            handleSourceTarget(ad[key]);
          });
      });
    };

    const transformed = _.cloneDeep(state.ads);
    transformed.forEach(handleAdsUnit);
    state.ads = transformed;
  },
  // 利用detail列表设置ads数组
  [types.SET_ADS_BY_DETAIL](state, adDetailList = []) {
    if (!adDetailList.length) {
      state.ads = _.cloneDeep(INIT_ADS);
      return;
    }

    const adsUnit = _.cloneDeep(adDetailList)
      .map((adDetail) => {
        const cta = adDetail.call_to_action;
        adDetail.enableUrl = Boolean(adDetail.external_url) || Boolean(adDetail.page_id);
        adDetail.call_to_action = (cta === 'view' || cta === 'download') ? 'learn_more' : cta; // 兼容旧创意的cta
        return adDetail;
      }).map(transformDetail2Ad); // adDetail => ad
    state.ads = [adsUnit]; // 只有一个对照组
  },
  // 利用detail列表设置ads数组
  // 这个方法与↑ 上面的方法作用一样，但是上面的方法在用于设置split test时有问题，所以这里写了一个适用于split test的方法
  [types.SET_ADS_BY_INDEX](state, { creativeDataList = [], splitTestTab = 0 }) {
    if (!creativeDataList.length) return;

    const newAds = _.cloneDeep(state.ads || []);
    const adsUnit = _.cloneDeep(creativeDataList)
      .map((ad) => {
        const cta = ad.call_to_action;
        ad.enableUrl = Boolean(ad.external_url) || Boolean(ad.page_id);
        ad.call_to_action = (cta === 'view' || cta === 'download') ? 'learn_more' : cta; // 兼容旧创意的cta
        return ad;
      }).map(transformDetail2Ad);

    newAds[splitTestTab] = adsUnit;
    state.ads = newAds;
  },
  // 添加一个ad，可能已有具体素材，也可能还是空的
  [types.ADD_AD](state, { splitTestTab, ad }) {
    state.ads[splitTestTab].push(_.cloneDeep(ad));
  },
  // 添加一个ad，可能已有具体素材，也可能还是空的
  [types.SET_APPINFO](state, data) {
    state.appInfo = data;
  },
  // 根据detail信息来添加一个ad
  [types.ADD_AD_BY_DETAIL](state, { splitTestTab, adDetail = {} }) {
    const ad = transformDetail2Ad(adDetail);
    state.ads[splitTestTab].push(ad);
  },
  // 添加一个ad，可能已有具体素材，也可能还是空的
  [types.UPDATE_AD](state, { splitTestTab, ad, index }) {
    state.ads[splitTestTab].splice(index, 1, _.cloneDeep(ad));
  },
  [types.UPDATE_AD_SOURCE](state, { splitTestTab, adSource = NORMAL_AD_SOURCES.CREATE_NEW.value, index }) {
    const ad = state.ads[splitTestTab][index] || {};
    ad.adSource = adSource;
    state.ads[splitTestTab].splice(index, 1, ad);
  },
  [types.UPDATE_AD_TYPE](state, {
    splitTestTab, adSource, adType = '', index,
  }) {
    const ad = state.ads[splitTestTab][index] || {};
    if (!isValidAdSource(adSource) || !ad[adSource]) {
      return;
    }

    ad[adSource].adType = adType;
    state.ads[splitTestTab].splice(index, 1, ad);
  },
  // 修改AD的具体属性值, 此时必定已经确定了adType，无需再二次校验
  [types.CHANGE_AD_PROPERTY](state, {
    splitTestTab, index, prop, value,
  }) {
    const ad = state.ads[splitTestTab][index] || {};
    const { adSource } = ad;
    if (!isValidAdSource(adSource)) {
      return;
    }
    const { adType = '' } = ad[adSource] || {};

    if (!adType || !ad[adSource][adType]) {
      return;
    }
    ad[adSource][adType][prop] = value;
    state.ads[splitTestTab].splice(index, 1, ad);
  },
  // 修改AD的具体属性值，adType手动设置
  // 原因：由于下沉后，fallback_type 需要保证 普通创意和原生创意都是一致的，因此需要在普通创意内，修改了原生创意的属性值
  [types.CHANGE_AD_PROPERTY_BY_AD_SOURCE](state, {
    splitTestTab, index, prop, value, adSource,
  }) {
    const ad = state.ads[splitTestTab][index] || {};
    if (!isValidAdSource(adSource)) {
      return;
    }
    const { adType = '' } = ad[adSource] || {};

    if (!adType || !ad[adSource][adType]) {
      return;
    }
    ad[adSource][adType][prop] = value;
    state.ads[splitTestTab].splice(index, 1, ad);
  },
  [types.CHANGE_CAROUSEL_IMAGE_AD_IMAGE_PROPERTY](state, {
    splitTestTab, index, prop, imageIndex, value,
  }) {
    const ad = state.ads[splitTestTab][index] || {};
    const { adSource } = ad;
    if (!isValidAdSource(adSource)) {
      return;
    }
    const { adType = '' } = ad[adSource] || {};

    if (!adType
      || !ad[adSource][adType]
      || !ad[adSource][adType].image_info
      || !ad[adSource][adType].image_info[imageIndex]) {
      return;
    }
    ad[adSource][adType].image_info[imageIndex][prop] = value;
    state.ads[splitTestTab].splice(index, 1, ad);
  },
  [types.DELETE_AD](state, { splitTestTab, index }) {
    state.ads[splitTestTab].splice(index, 1);
  },
  // 商品目录对应的词包列表
  [types.SET_DPA_WORD_LIST](state, data) {
    state.dpaWordList = data;
  },
  [types.SET_DPA_WORD_LIST](state, data) {
    state.dpaWordList = data;
  },
  [types.SET_AD_COPY_NUMBER](state, data) {
    state.copyNumber = data;
  },
  [types.SET_SHOULD_QC_OPTIMIZATION_TOOLS_TIPS_SHOW](state, data) {
    state.shouldQcOptimizeToolsTipsShow = data;
  },
  [types.SET_PRECHECK_SUGGESTIONS](state, data) {
    state.precheckSuggestions = data;
  },
  [types.SET_REAL_TIME_PRECHECK_SUGGESTIONS](state, data) {
    _.set(state, `realTimePrecheckSuggtions[${data.index}][${data.prop}]`, data);
    // 这里的index标识这个实时校验结果是哪一个广告下的，用一次就可以废弃掉了，后续拼接到素材预校验的数组中的index来标识就可以了
    delete data.index;
  },
  [types.DELETE_REAL_TIME_PRECHECK_SUGGESTIONS](state, data) {
    state.realTimePrecheckSuggtions.splice(data.index, 1);
  },
  [types.SET_HAS_INTO_AD](state, data) {
    state.hasIntoAd = data;
  },
  [types.SET_PRECHECK_SUMMARY](state, data) {
    state.precheckSummary = data;
  },
  [types.SET_PRODUCT_VIDEO_AND_NO_VIDEO_PKG](state, data) {
    state.isNoVideoPkgWarnTipShow = data;
  },
  [types.SET_NO_PRODUCT_VIDEO_AND_NO_VIDEO_PKG](state, data) {
    state.isCurrentProductSetCannotPreview = data;
  },
  [types.SET_IS_PRODUCT_SET_NO_PRODUCT](state, data) {
    state.isProductSetNoProduct = data;
  },
  [types.SET_CHANGE_VIDEO_PKG_BTN_LOADING](state, data) {
    state.isChangeVideoPkgBtnLoading = data;
  },
  [types.SET_ZEFR_COUNTRY](state, data) {
    state.supportBsZefrCountryList = data;
  },
  [types.SET_BS_IAS_COUNTRY](state, data) {
    state.supportBsIasCountryList = data;
  },
  [types.SET_BS_DV_COUNTRY](state, data) {
    state.supportBsDvCountryList = data;
  },
  [types.SET_ZEFR_AUTHORITY](state, data) {
    state.hasZefrAuthority = data;
  },
  [types.SET_BSIAS_AUTHORITY](state, data) {
    state.hasBsIasAuthority = data;
  },
  [types.SET_BS_DV_AUTHORITY](state, data) {
    state.hasBsDvAuthority = data;
  },
  [types.SET_SHOW_PROTOCOL_TERMS](state, status) {
    state.showProtocolTerms = status;
  },
  [types.SET_LOADING_PROMOTION_DETAIL](state, status) {
    state.loadingPromotionDetail = status;
  },
  [types.SET_BIND_ACCOUNT_DATA](state, data) {
    state.bindAccountData = data;
  },
  [types.SET_IDENTITY_AUTHORIZATION_STATE](state, status) {
    state.identityAuthorizationState = status;
  },
  [types.SET_SHOP_AUTHORIZATION_STATE](state, status) {
    state.shopAuthorizationState = status;
  },
  [types.SET_SHOP_AUTHORIZATION_CHECKING](state, status) {
    state.shopAuthorizationChecking = status;
  },
  [types.SET_SHOP_ADS_HAS_PERMISSION](state, status) {
    state.shopAdsHasPermission = status;
  },
  [types.SET_SHOP_PRODUCTS_NUM_IS_VALID](state, status) {
    state.shopProductsNumIsValid = status;
  },
  [types.CHANGE_ADS_RENDER_TASK](state, {
    splitTestTab, index, taskList = [], isLastAdEmpty,
  }) {
    if (!taskList.length) return;
    const { ADD_NEW_CREATIVE } = VIDEO_ASYNC_RENDER_FILL_ACTION_TYPE;
    const hasAddCreative = taskList
      .some(task => task.taskActionType === ADD_NEW_CREATIVE);
    if (hasAddCreative && isLastAdEmpty) state.ads[splitTestTab].pop();
    const ad = _.cloneDeep(state.ads?.[splitTestTab]?.[index] || INIT_ADS[splitTestTab][index]);
    const { adSource } = ad;
    // 视频渲染只能是单视频创意
    const adType = AD_TYPE.SINGLE_VIDEO.value;
    const adInfoBase = ad[adSource][adType];
    if (!adInfoBase) return;
    adInfoBase.creative_name = '';
    adInfoBase.video_info = {};
    adInfoBase.image_info = [];
    taskList.forEach((task) => {
      const { taskActionType, taskId, coverUri } = task;
      if (taskActionType === ADD_NEW_CREATIVE) {
        const newAd = _.cloneDeep(ad);
        newAd[adSource][adType].taskId = taskId;
        newAd[adSource][adType].image_info = [{ web_uri: coverUri }];
        state.ads[splitTestTab].push(newAd);
      } else {
        ad[adSource][adType].taskId = taskId;
        ad[adSource][adType].image_info = [{ web_uri: coverUri }];
        state.ads[splitTestTab].splice(index, 1, ad);
      }
    });
  },
  [types.SET_ADS_ASYNC_RENDER_VIDEO_TASK_IDS](state, { splitTestTab, taskList = [] }) {
    if (!taskList.length) return;
    // 找到已有的UIDs
    const existedUids = taskList?.map(({ taskId }) => taskId);
    // 排除掉已有的旧的，避免重复添加
    state.videoRenderInfoList[splitTestTab].taskList = state.videoRenderInfoList[splitTestTab].taskList
      ?.filter(({ taskId }) => !existedUids.includes(taskId));
    // 把新增的添加一下
    state.videoRenderInfoList[splitTestTab].taskList = state
      .videoRenderInfoList[splitTestTab].taskList.concat(taskList);
  },

  [types.FILL_VIDEO_FULL_INFO_BY_TASK_ID](state, { splitTestTab, videoFullInfo }) {
    const adUnit = state.ads?.[splitTestTab];
    adUnit.forEach((ad) => {
      const { adSource } = ad;
      // 回填视频信息，则创意类型一定是单视频创意
      const adType = AD_TYPE.SINGLE_VIDEO.value;
      const adDetail = ad[adSource][adType];
      if (adDetail.taskId === videoFullInfo.taskId) {
        Object.assign(adDetail, videoFullInfo);
      }
    });
  },
  [types.CHANGE_VIDEO_RENDER_INFO_PROP](state, {
    splitTestTab, taskId, prop, value,
  }) {
    const list = state.videoRenderInfoList[splitTestTab].taskList;
    const targetTask = list.find(task => task.taskId === taskId);
    if (targetTask) {
      const newTask = {
        ...targetTask,
        [prop]: value,
      };
      state.videoRenderInfoList[splitTestTab].taskList = list
        .filter(task => task.taskId !== taskId)
        .concat([newTask]);
    }
  },
  [types.DELETE_VIDEO_RENDER_TASK_BY_TASK_ID](state, { splitTestTab, taskId }) {
    state.videoRenderInfoList[splitTestTab].taskList = state.videoRenderInfoList[splitTestTab].taskList
      .filter(task => task.taskId !== taskId);
  },
  /**
   * 同步ad内所有adType中的identity数据
   */
  [types.SET_IDENTITY_INFO_BY_AD_TYPE](state, { splitTestTab, index }) {
    const ad = _.cloneDeep(state.ads[splitTestTab][index] || {});
    const sourceTarget = _.get(ad, ad?.adSource);
    if (!sourceTarget) return;

    const currentType = sourceTarget?.adType;
    if (!currentType) return;

    const currentAdDetail = sourceTarget[currentType] || {};

    // ad内包含多种形式的素材，单图、单视频、商品库图片、商品库视频，每种形式的identity数据都需要同步
    Object.keys(sourceTarget)
      .filter(key => key !== 'adType')
      .forEach((key) => {
        const adDetail = sourceTarget[key];
        adDetail.identity_id = currentAdDetail?.identity_id || '';
        adDetail.identity_type = currentAdDetail?.identity_type || 0;
        adDetail.identity_bc_id = currentAdDetail?.identity_bc_id ?? 0;
        adDetail.identity_data = currentAdDetail?.identity_data || {};
        adDetail.avatar_icon = currentAdDetail?.avatar_icon;
        adDetail.app_name = currentAdDetail?.app_name || '';
        adDetail.source = currentAdDetail?.source || '';
      });
    state.ads[splitTestTab].splice(index, 1, ad);
  },
  [types.CHECK_HAS_LEAD_ADS](state, data) {
    state.hasLeadAds = data;
  },
  [types.SET_IS_SHOP_ADS_LIVE_SCENARIO](state, value) {
    state.isShopAdLiveScenario = value;
  },
  [types.SET_CREATIVES](state, list) {
    state.creatives = list;
  },
  [types.SET_FROM_LIBRARY_VID_LIST](state, value) {
    state.fromLibraryVid = value;
  },
  [types.SET_FROM_LIBRARY_CARD_ID_LIST](state, value) {
    state.fromLibraryCardId = value;
  },
  [types.SET_FROM_LIBRARY_PAGE_ID_LIST](state, value) {
    state.fromLibraryPageId = value;
  },
  [types.SET_IDENTITY_EMPTY](state, data) {
    state.identityEmpty = data;
  },
};
